import { Alert, Button, CircularProgress, Link, Paper, Stack, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import get from 'lodash-es/get';
import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { batchUpdate } from '../../../api/ventura/destination';
import { batchProgress } from '../../../api/ventura/location';
import { API_S3_DESTINATION_UPLOAD_INFO_URL } from '../../../constants/apiUrls';
import { useConfig } from '../../../hooks/useConfig';
import { useConstants } from '../../../hooks/useConstants';
import { removeFileExtension } from '../../../util/helpers';
import { snakeCaseKeys } from '../../../util/object';
import { uploadFile } from '../../../util/services';
import FormEndOptions from '../../forms/FormEndOptions';
import S3Upload from '../../forms/S3Upload';

interface DestinationsFormProps {
  adGroupId: number;
  adGroupName: string;
  lineItemId: number;
  onClose: Function;
}

function DestinationsForm({ adGroupId, adGroupName, lineItemId, onClose }: DestinationsFormProps) {
  const [valid, setValid] = useState(false);
  const [status, setStatus] = useState('');
  const [severity, setSeverity] = useState(undefined);
  const { constants } = useConstants();
  const { apiHost } = useConfig();
  const VENTURA_UPLOADS_PREFIX_DESTINATIONS = get(constants, 'DESTINATION.VENTURA_UPLOADS_PREFIX_DESTINATIONS', '');

  const form = useForm({
    defaultValues: {
      adGroupId,
      key: undefined,
    },
  });

  const { handleSubmit, getValues } = form;

  function clean(field) {
    return field.trim().replace(/[^A-Za-z0-9\-. @|!~[\]_]/g, '');
  }

  const generateUploadKey = () => {
    const cleanName = clean(adGroupName);
    return `${VENTURA_UPLOADS_PREFIX_DESTINATIONS}${lineItemId}/${adGroupId}_${new Date().getTime()}_${cleanName}`;
  };

  const onS3UploadFailure = ({ message }) => {
    setValid(false);
    setSeverity('error');
    setStatus(message);
  };

  const validateDuplicates = async (files) => {
    const url = '/admin/locations/validate_duplicate_name';
    const file = files[0];
    const response = await uploadFile(apiHost, url, null, file);
    /*
    Here is a typical response
    {"valid":true}
    or
    { 
      "valid": false,
      "title": "Invalid POI Ids validation",
      "message": "The upload file contains the following invalid poi IDs: 100, 200"
    }
    */

    const isValid = get(response, 'valid', false);
    setValid(isValid);
    if (isValid) {
      setSeverity('success');
      setStatus('Success. Please proceed to upload the file.');
    } else {
      const title = get(response, 'title', '');
      const message = get(response, 'message', '');
      setSeverity('error');
      setStatus(`${title}: ${message}`);
    }
  };

  const checkProgress = (response) => {
    const responseStatus = get(response, 'status', '');
    if (responseStatus.startsWith('Completed')) {
      setSeverity('success');
    } else {
      setSeverity('info');
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      setTimeout(() => batchJobProgress.mutate(), 5000);
    }
    setStatus(responseStatus);
  };

  const batchJobProgress = useMutation({
    mutationFn: () => batchProgress(apiHost, removeFileExtension(getValues('key'))),
    onSuccess: (response) => {
      checkProgress(response);
    },
  });

  const upload = useMutation({
    mutationFn: (payload) => batchUpdate(apiHost, adGroupId, payload),
    onSuccess: (response) => {
      checkProgress(response);
      setValid(false);
    },
  });

  const onSubmit = (formData) => {
    const payload = snakeCaseKeys(formData);
    upload.mutate(payload);
  };

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <FormProvider {...form}>
      <Stack spacing={2}>
        <Typography variant="h5" fontWeight="bold">
          Update Destinations for {adGroupName}
        </Typography>
        <Paper>
          <Stack m={2}>
            <Typography variant="h5">Step 1: Attach File</Typography>
            <Typography my={2}>
              <ul>
                <li>
                  Uploading file will delete all existing destinations. Use download link above to download current
                  destinations.
                </li>
                <li>
                  Actions
                  <ul>
                    <li>
                      Add: Add a new line to file with name, latitude, longitude, address (optional) and leave Id
                      field/5th column blank
                    </li>
                    <li>Remove: Remove line from file</li>
                    <li>Edit: You can change the name or address of a destination only.</li>
                  </ul>
                </li>
                <li>File format: Name, Latitude, Longitude, Address, Id (optional)</li>
                <li>Header row is required.</li>
                <li>Processing will happen in background and email will be sent when finished.</li>
              </ul>
            </Typography>
            <Stack spacing={2}>
              <S3Upload
                name="key"
                s3InfoUrl={API_S3_DESTINATION_UPLOAD_INFO_URL}
                generateKey={generateUploadKey}
                accept=".xlsx,.csv"
                onFailure={onS3UploadFailure}
                onStart={validateDuplicates}
              />
              <Stack direction="row" justifyContent="center">
                <Link href={`${apiHost}/admin/campaigns/${adGroupId}/destinations/index_csv`}>
                  <Button variant="outlined">Download Existing Destinations</Button>
                </Link>
              </Stack>
            </Stack>
          </Stack>
        </Paper>
        <Paper sx={{ width: 1 }}>
          <Stack m={2} spacing={2}>
            <Typography variant="h5">Step 2: Upload File</Typography>
            {status && (
              <Stack direction="row" m={2}>
                <Alert
                  sx={{ width: '100%' }}
                  severity={severity}
                  action={severity === 'info' ? <CircularProgress color="inherit" size={24} /> : undefined}
                >
                  {status}
                </Alert>
              </Stack>
            )}
            <Stack direction="row" justifyContent="center">
              <Button disabled={!valid} onClick={handleSubmit(onSubmit)}>
                Upload
              </Button>
            </Stack>
          </Stack>
        </Paper>
        <FormEndOptions>
          <Stack direction="row" justifyContent="center" width="100%" spacing={2} pt={2}>
            <Button variant="outlined" onClick={() => onClose()}>
              Close
            </Button>
          </Stack>
        </FormEndOptions>
      </Stack>
    </FormProvider>
  );
}

export default DestinationsForm;
